import type { ICockpitBootstrapData, IContactPerson, IGetUserDetailsResult, IUser } from '@condo/domain';
import { bootstrapDataUserCompressor } from '@condo/domain';
import { getClient } from '@/shared/api/client';

export interface UpdateOwnProfileParams {
    firstname?: string;
    lastname?: string;
    avatarMediaFileId?: number | null;
}

/**
 * Fetch current user details from basecamp (includes roles).
 * Uses /users/me which returns the authenticated user.
 */
export const getCurrentUserDetails = async (): Promise<IGetUserDetailsResult> => {
    const { data } = await getClient('basecamp').get<IGetUserDetailsResult>('/users/me');
    return data;
};

/**
 * Update the authenticated user's own contact profile (name, avatar).
 * Cockpit parity: PATCH /users/me/profile
 */
export const updateOwnProfile = async (params: UpdateOwnProfileParams): Promise<IContactPerson> =>
    getClient('basecamp')
        .patch('/users/me/profile', params)
        .then(response => response.data.contact);

// Types for processed users (matching Vue project)
export interface IUsersObject {
    [uid: string]: IUser;
}

export interface IContactUsersObject {
    [contactId: string]: IUser;
}

/**
 * Process users list into organized objects
 * Ported from Vue project: /services/core/cockpit/src/shared/helpers.ts processUsersList
 */
export const processUsersList = (users: IUser[]) => {
    const baseUsers = users.filter(u => !!u.email);
    const usersObject = baseUsers.reduce((acc, user) => {
        acc[user.uid] = user;
        return acc;
    }, {} as IUsersObject);

    return {
        usersObject,
        usersByContact: baseUsers
            .filter(u => u.contact?.contactId)
            .reduce((acc, user) => {
                acc[user.contact!.contactId!] = user;
                return acc;
            }, {} as IContactUsersObject),
        companyFunctions: users.filter(u => !!u.contact?.companyFunction).map(u => ({ ...u.contact!.companyFunction, contact: u.contact })),
    };
};

/**
 * Request password reset email via basecamp API.
 * Endpoint is skipAuth - works for unauthenticated users.
 */
export const resetUserPassword = async (query: { email?: string; continueUrl?: string; uid?: string }): Promise<void> => {
    await getClient('basecamp').post(`/users/reset-password`, {
        email: query.email,
        uid: query.uid,
        continueUrl: query.continueUrl,
    });
};

/**
 * Fetch bootstrap data (including users) from basecamp API
 * Ported from Vue project: /services/core/cockpit/src/api/basecamp.api.ts fetchBootstrapData
 */
export const fetchBootstrapData = async (): Promise<ICockpitBootstrapData> => {
    try {
        const compressedData = await getClient('basecamp')
            .get('/dictionaries/bootstrap-data')
            .then(res => res.data);

        // Decompress users data using the bootstrap data user compressor
        const users = bootstrapDataUserCompressor.decompress(compressedData.users);
        const data = { ...compressedData, users } as ICockpitBootstrapData;

        return data;
    } catch (error) {
        console.error('Failed to fetch bootstrap data:', error);
        throw error;
    }
};
